import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Circle, Check, FileText } from 'lucide-react'

interface TimelineMessage {
  id: number
  body: string
  messageType: string
  createdAt: string
  metadata?: Record<string, unknown>
  milestone?: {
    id: number
    name: string
  } | null
  author?: {
    id: string
    name: string
    avatar: string | null
  } | null
}

interface TimelineCardProps {
  timeline: TimelineMessage[]
}

const formatDate = (dateString: string) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateString))
}

export const TimelineCard: React.FC<TimelineCardProps> = ({ timeline }) => {
  if (!timeline || timeline.length === 0) {
    return null
  }

  return (
    <Card className="bg-gray-900/90 backdrop-blur-xs border border-white/5 rounded-3xl rounded-bl-none">
      <CardHeader className="py-3">
        <CardTitle className="text-base text-white">Activity Timeline</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {timeline.map((message, idx) => {
            const isLast = idx === timeline.length - 1
            return (
              <div key={message.id} className="relative flex gap-2 py-2">
                {/* Timeline line */}
                {!isLast && <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-gray-200" />}

                {/* Icon */}
                <div className="shrink-0 z-10">
                  {message.messageType === 'system' && (
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                      <Circle className="h-3 w-3 text-blue-600" />
                    </div>
                  )}
                  {message.messageType === 'status_change' && (
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                  )}
                  {message.messageType === 'review' && (
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <FileText className="h-3 w-3 text-purple-600" />
                    </div>
                  )}
                  {message.messageType === 'user' && message.author && (
                    <Avatar className="h-6 w-6 border border-gray-200">
                      {message.author.avatar && <AvatarImage src={message.author.avatar} />}
                      <AvatarFallback className="text-xs">
                        {message.author.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>

                {/* Content - More compact */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-sm font-medium text-white">{message.body}</p>
                    {message.milestone && (
                      <span className="text-xs text-gray-400">· {message.milestone.name}</span>
                    )}
                    {message.author && (
                      <span className="text-xs text-gray-400">· {message.author.name}</span>
                    )}
                    <span className="text-xs text-gray-500">
                      · {formatDate(message.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
